import React, { useMemo, useState, useEffect } from 'react';
import { Plus, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { useAvailableNodes } from '../../../../hooks/useAvailableNodes';
import { DynamicIcon } from './Nodes/DynamicIcon';
import * as Icons from 'lucide-react';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';

interface NodeToolbarProps {
    onNodeAdd: (nodeType: string, nodeData?: Record<string, any>) => void;
}

const NodeToolbar: React.FC<NodeToolbarProps> = ({ onNodeAdd }) => {
    const { nodes, loading, error } = useAvailableNodes();
    const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({});

    // Group nodes by category
    const categorizedNodes = useMemo(() => {
        const categories: Record<string, typeof nodes> = {};
        nodes.forEach((node: any) => {
            const category = node.ui_settings.category || 'Other';
            if (!categories[category]) {
                categories[category] = [];
            }
            categories[category].push(node);
        });
        return categories;
    }, [nodes]);

    // Initialize all categories as open
    useEffect(() => {
        if (Object.keys(categorizedNodes).length > 0 && Object.keys(openCategories).length === 0) {
            const initialOpen: Record<string, boolean> = {};
            Object.keys(categorizedNodes).forEach((category) => {
                initialOpen[category] = true;
            });
            setOpenCategories(initialOpen);
        }
    }, [categorizedNodes]);

    const toggleCategory = (category: string) => {
        setOpenCategories((prev) => ({
            ...prev,
            [category]: !prev[category],
        }));
    };

    const handleNodeAdd = (node: any) => {
        onNodeAdd("custom", {
            id: node.id,
            label: node.default_name,
            description: "Click to edit",
            icon: node.ui_settings.icon,
            category: node.ui_settings.category,
        });
    };

    if (loading) {
        return (
            <div className="p-4 flex items-center justify-center" style={{ minHeight: '200px' }}>
                <Loader2 size={24} className="animate-spin text-primary" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4">
                <div
                    className="text-sm rounded-lg p-3 bg-destructive/10 text-destructive font-medium border border-destructive/20"
                >
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="p-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center text-foreground">
                <Plus size={18} className="mr-2" />
                Add Node
            </h3>
            <div className="space-y-2">
                {Object.keys(categorizedNodes).length === 0 ? (
                    <div className="text-sm text-center p-4 text-muted-foreground">
                        No nodes available
                    </div>
                ) : (
                    Object.entries(categorizedNodes).map(([category, categoryNodes]) => (
                        <Collapsible
                            key={category}
                            open={openCategories[category] ?? true}
                            onOpenChange={() => toggleCategory(category)}
                        >
                            <CollapsibleTrigger
                                className={`w-full flex items-center justify-between p-2 rounded-lg transition-colors text-foreground hover:bg-muted/15 ${openCategories[category] ? 'bg-muted/10' : 'bg-transparent'}`}
                            >
                                <span className="font-semibold text-sm">{category}</span>
                                {openCategories[category] ? (
                                    <ChevronDown size={16} />
                                ) : (
                                    <ChevronRight size={16} />
                                )}
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                                <div className="space-y-2 mt-2">
                                    {categoryNodes.map((node: any) => (
                                        <button
                                            key={node.id}
                                            onClick={() => handleNodeAdd(node)}
                                            className="w-full p-3 rounded-lg border text-left transition-colors flex items-start gap-3 border-border bg-background text-foreground hover:bg-muted/10 hover:border-primary"
                                        >
                                            <div className="mt-0.5 flex-shrink-0 text-primary">
                                                <DynamicIcon
                                                    name={node.ui_settings.icon as keyof typeof Icons}
                                                    size={16}
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-foreground">
                                                    {node.default_name}
                                                </div>
                                                <div className="text-xs mt-1 text-muted-foreground">
                                                    {node.description}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </CollapsibleContent>
                        </Collapsible>
                    ))
                )}
            </div>
        </div>
    );
};

export default NodeToolbar;

